import { useRouter } from "next/router";
import { createPortal } from "react-dom";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useEffectOnce, useHover } from "usehooks-ts";
import { Button, theme } from "antd";
import {
  CloseOutlined,
  ExclamationCircleOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
  ReloadOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import {
  ToasterContext,
  ToasterMessageType,
  ToasterMessage,
  useToaster,
  useToasterContext,
} from "src/hooks/useToaster";
import { useOrg, Org } from "src/hooks/useOrg";
import { usePopup, PopupContext } from "src/hooks/usePopup";
import { useApplicationContext } from "src/hooks/useApplication";
import { useDocumentHeight } from "src/hooks/useDocumentHeight";
import { Flex } from "src/components/Flex";
import { COLLAPSED_MENU_WIDTH, MENU_WIDTH } from "./Menu";
import { usePreferences } from "src/hooks/usePreferences";
import { Menu } from "./Menu";

export function AppScreen(props: Props) {
  const org = useOrg();
  const router = useRouter();
  const noPaddingRoutes = ["/[orgId]/home"];
  const shouldPad = !noPaddingRoutes.includes(router.pathname);
  return (
    <AppStatusNotifier>
      <AppMessages>
        <AppPopup>
          <ScreenWithMenu {...props}>
            {props.main(org.id ? org : undefined)}
          </ScreenWithMenu>
        </AppPopup>
      </AppMessages>
    </AppStatusNotifier>
  );
}

function ScreenWithMenu(props: PropsWithChildren<Props>) {
  const { token } = theme.useToken();
  const { sidebar } = useApplicationContext();
  const router = useRouter();
  //want to skip padding for home page. total hack, but necessary without
  //a larger refactor.
  const noPaddingRoutes = ["/[orgId]/home"];
  //treat home page as a special case, with a different background color
  //will want to apply this to all pages eventually
  const backgroundColor = noPaddingRoutes.includes(router.pathname)
    ? "var(--color-bg-base-2)"
    : "var(--color-bg-base)";

  return (
    <>
      <div id="screen-insert" style={{ position: "relative" }} />
      <Flex
        direction="column"
        data-approle="screen-with-menu"
        style={{
          height: "100vh",
          width: "100%",
          color: token.colorText,
          overflow: "hidden",
        }}
      >
        {props.side === undefined ? <AppMenu /> : props.side}
        <div
          className="thin_scrollbar"
          style={{
            flex: 1,
            width: "100%",
            minHeight: 0,
            maxHeight: "100%",
            overflow: "auto",
            padding: token.paddingSM,
            backgroundColor: backgroundColor,
            paddingLeft:
              props.side === undefined
                ? sidebar.isOpen
                  ? MENU_WIDTH + token.paddingSM
                  : COLLAPSED_MENU_WIDTH + token.paddingSM
                : 16,
          }}
        >
          {props.children}
        </div>
      </Flex>
    </>
  );
}

function AppStatusNotifier(props: PropsWithChildren) {
  const { token } = theme.useToken();
  const { refreshRequired } = useApplicationContext();
  return (
    <>
      {props.children}
      {refreshRequired &&
        createPortal(
          <div
            style={{
              position: "absolute",
              width: "100%",
              zIndex: 9001,
              bottom: 0,
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                display: "inline-block",
                padding: 8,
                borderRadius: 16,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                backgroundColor: token.colorBgMask,
                color: token.colorTextLightSolid,
                pointerEvents: "initial",
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  padding: 16,
                  borderRadius: 16,
                  backgroundColor: token.colorBgMask,
                  color: token.colorTextLightSolid,
                  pointerEvents: "initial",
                }}
              >
                <Flex gap={16} style={{ marginBottom: 24 }}>
                  <InfoCircleFilled
                    style={{
                      fontSize: 24,
                      color: token.colorBgBase,
                    }}
                  />
                  <span>A new version of the application is available.</span>
                </Flex>
                <Button
                  onClick={() => window.location.reload()}
                  icon={<ReloadOutlined />}
                >
                  Reload
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

function AppMessages(props: PropsWithChildren) {
  const { token } = theme.useToken();
  const toaster = useToaster();
  const visible = toaster.messages.filter(
    (message) => message.status === "new"
  );
  const hasVisibleMessages = visible.length > 0;
  const hoverRef = useRef(null);
  const hovering = useHover(hoverRef);
  const prefs = usePreferences();
  return (
    <ToasterContext.Provider value={toaster}>
      {props.children}
      <div ref={hoverRef}>
        {hasVisibleMessages && (
          <div
            style={{
              position: "absolute",
              bottom: 20,
              right: 20,
              backgroundColor: token.colorBgBase,
              borderRadius: 8,
              boxShadow: hovering ? undefined : token.boxShadowTertiary,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: 6,
              padding: 6,
              opacity: hovering ? 1 : 0.7,
            }}
          >
            {visible.map((message) => (
              <div
                key={message.key}
                style={{
                  backgroundColor: token.colorBgContainer,
                  borderRadius: 6,
                  display: "flex",
                  gap: 16,
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  padding: "16px 24px",
                  fontSize: 14,
                  boxShadow: hovering ? token.boxShadowTertiary : undefined,
                }}
              >
                <SelfDestructingMessage
                  message={message}
                  hovering={hovering}
                  timeout={prefs.query.data?.notifTimeout || 0}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </ToasterContext.Provider>
  );
}

function SelfDestructingMessage({
  hovering,
  message,
  timeout,
}: {
  hovering: boolean;
  message: ToasterMessage;
  timeout: number;
}) {
  const app = useApplicationContext();
  const toaster = useToasterContext();
  const { token } = theme.useToken();
  useEffect(() => {
    if (timeout > 0) {
      const tmr = setTimeout(
        () => toaster.dismissMessage(message),
        timeout * 1000
      );
      return () => clearTimeout(tmr);
    }
  }, [toaster, message, timeout]);
  return (
    <>
      <div>
        {message.type === ToasterMessageType.ERROR ? (
          <ExclamationCircleOutlined
            style={{
              height: 40,
              width: 40,
              fontSize: 40,
              color: token.colorErrorText,
            }}
          />
        ) : message.type === ToasterMessageType.UNDO ? (
          <UndoOutlined
            style={{
              height: 40,
              width: 40,
              fontSize: 40,
              color: token.colorText,
            }}
          />
        ) : (
          <InfoCircleOutlined
            style={{
              height: 40,
              width: 40,
              fontSize: 40,
              color: token.colorInfo,
            }}
          />
        )}
      </div>
      <div style={{ flex: 1, maxWidth: 320 }}>
        {message.type !== ToasterMessageType.UNDO && (
          <div
            style={{
              color:
                message.type === ToasterMessageType.ERROR
                  ? token.colorErrorText
                  : token.colorInfoText,
              fontWeight: 500,
            }}
            className="trunc"
          >
            {message.type}
          </div>
        )}
        <div
          style={{
            color:
              message.type === ToasterMessageType.ERROR
                ? token.colorTextTertiary
                : token.colorText,
          }}
          className="trunc"
        >
          {message.message}
        </div>
        {message.type === ToasterMessageType.UNDO && (
          <Button
            size="small"
            disabled={!message.undoType || !message.undoData}
            onClick={() => {
              if (message.undoType === "restore-tab") {
                app.appendTab(
                  message.undoData!.current_title,
                  message.undoData!.current_url
                );
              }
              toaster.dismissMessage(message);
            }}
          >
            Undo Action
          </Button>
        )}
      </div>
      <Button type="text" onClick={() => toaster.dismissMessage(message)}>
        <CloseOutlined style={{ opacity: hovering ? 1 : 0.3 }} />
      </Button>
    </>
  );
}

function AppPopup(props: PropsWithChildren) {
  const { token } = theme.useToken();
  const popup = usePopup();
  const ready = popup.isOpen && popup.content !== null;

  useEffectOnce(() => {
    document.body.style.backgroundColor = token.colorBgBase;
    document.body.style.overflowX = "hidden";
  });

  const documentHeight = useDocumentHeight();

  return (
    <PopupContext.Provider value={popup}>
      <>
        {ready &&
          createPortal(
            <div
              id="app-popup-mask"
              style={{
                position: "absolute",
                width: "100vw",
                // Fallback to 100vh if something goes wrong with measurement
                height: documentHeight ? `${documentHeight}px` : "100vh",
                zIndex: 1001,
                overflow: "hidden",
              }}
              onClick={(e) => {
                if (e.target === document.querySelector("#app-popup-mask")) {
                  popup.close();
                }
              }}
            >
              <div
                id="app-popup-container"
                style={{
                  display: "inline-block",
                  position: "relative",
                  visibility: popup.size.width > 0 ? "visible" : "hidden",
                  backgroundColor: token.colorBgElevated,
                  borderRadius: token.borderRadiusLG,
                  boxShadow: token.boxShadow,
                  top: popup.position!.top,
                  left: popup.position!.left,
                  width: popup.size.width || "auto",
                  height: popup.size.height || "auto",
                }}
              >
                {popup.content!}
              </div>
            </div>,
            document.querySelector("#screen-insert")!
          )}
        {props.children}
      </>
    </PopupContext.Provider>
  );
}

function AppMenu() {
  const router = useRouter();
  const app = useApplicationContext();
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let waiting = false;
    const end = () => {
      waiting = false;
      setLoading(false);
    };
    const start = () => {
      waiting = true;
      setTimeout(() => {
        if (waiting) setLoading(true);
      }, 10);
    };
    router.events.on("routeChangeStart", start);
    router.events.on("routeChangeComplete", end);
    router.events.on("routeChangeError", end);
    return () => {
      router.events.off("routeChangeStart", start);
      router.events.off("routeChangeComplete", end);
      router.events.off("routeChangeError", end);
    };
  }, [router.events]);

  return (
    <Flex start gap={8} style={{ width: "100%" }}>
      <div
        style={{
          height: 0,
          overflow: "visible",
          width: app.sidebar.isOpen ? MENU_WIDTH : COLLAPSED_MENU_WIDTH,
        }}
      >
        <Menu loading={loading} orgId={app.org_id} />
      </div>
      <div style={{ flex: 1 }} id="app-header"></div>
    </Flex>
  );
}

type Props = {
  title?: string;
  side?: React.ReactNode;
  orgId?: boolean;
  main: (org?: Org) => React.ReactNode;
};
